import { SvgIconProps, TextField as MaterialTextField, TextFieldProps as MaterialTextFieldProps } from '@mui/material';
import React, {
  ChangeEvent,
  ChangeEventHandler,
  FocusEvent,
  FocusEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { TooltipButton } from '../TooltipButton/TooltipButton';

/**
 * TestFieldProps
 *
 * Note that repeating the EventHandler here is merely for Storybook,
 * so they are wrapped by the action plugin
 */

export interface TextFieldProps extends Omit<MaterialTextFieldProps, 'margin' | 'FormHelperTextProps'> {
  /**
   * Set a tooltip and show a TooltipButton as endAdornment
   */
  tooltip?: string;

  /**
   * Set a symbol and show Icon as startAdornment
   */
  symbol?: React.ReactElement<SvgIconProps> | string;

  /**
   * Max Char Count Size
   */
  maxLength?: HTMLInputElement['maxLength'];

  /**
   * Min Char Count Size
   */
  minLength?: HTMLInputElement['minLength'];

  /**
   * Show a Text Counter
   */
  showCurrentLength?: boolean;

  /**
   * Show a Text Counter
   */
  showMaxLength?: boolean;

  /**
   * Focus Event: onBlur
   * @see [https://reactjs.org/docs/events.html#onblur]
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Focus Event: onFocus
   * @see [https://reactjs.org/docs/events.html#onfocus]
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * Change Event: onChange
   * @see [https://reactjs.org/docs/events.html#onchange]
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

/**
 * The TextField
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/form-komponenten/textfield]
 */
export const TextField = React.memo(
  React.forwardRef<HTMLInputElement, TextFieldProps>(
    (
      {
        variant = 'outlined',
        tooltip,
        symbol,
        slotProps,
        onFocus,
        onBlur,
        onChange,
        error,
        maxLength,
        minLength,
        showCurrentLength,
        showMaxLength,
        helperText,
        inputMode,
        inputProps: deprecatedHtmlInputProps, // @deprecated
        InputProps: deprecatedInputProps, // @deprecated
        InputLabelProps: deprecatedInputLabelProps, // @deprecated
        ...props
      },
      ref
    ) => {
      // *** HTML INPUT HANDLING

      const HtmlInputProps = useMemo(() => {
        return { maxLength, minLength, inputMode, ...deprecatedHtmlInputProps, ...slotProps?.htmlInput };
      }, [deprecatedHtmlInputProps, maxLength, minLength, inputMode, slotProps?.htmlInput]);

      // *** TOOLTIP & ICON HANDLING

      const [startAdornmentWidth, setStartAdornmentWidth] = useState<number>(0);
      const setStartAdornmentRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
          setStartAdornmentWidth(node.offsetWidth + 16);
        }
      }, []);

      const InputProps = useMemo(() => {
        // Info: We do not use InputAdornment here, due an hydration error in mui
        // this should mostly behave them same
        const endAdornment = tooltip ? (
          <div className="MuiInputAdornment-root MuiInputAdornment-positionStart" style={{ marginRight: '9px' }}>
            <TooltipButton title={tooltip} />
          </div>
        ) : undefined;

        // Info: We do not use InputAdornment here, due an hydration error in mui
        // this should mostly behave them same
        const startAdornment = symbol ? (
          <div
            className="MuiInputAdornment-root MuiInputAdornment-disablePointerEvents MuiInputAdornment-positionEnd"
            style={{ marginLeft: '9px' }}
            ref={setStartAdornmentRef}
          >
            {symbol}
          </div>
        ) : undefined;

        if (variant === 'filled') {
          return { disableUnderline: true, endAdornment, startAdornment, ...deprecatedInputProps, ...slotProps?.input };
        }
        return { endAdornment, startAdornment, ...deprecatedInputProps, ...slotProps?.input };
      }, [deprecatedInputProps, tooltip, symbol, setStartAdornmentRef, variant, slotProps?.input]);

      // *** STATE HANDLING

      const [focused, setFocused] = useState(false);
      const [length, setLength] = useState(String(props?.value || props?.defaultValue || '').length);

      const onFocusCallback = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
          setFocused(true);
          setLength(event?.target?.value?.length || 0);
          if (onFocus) {
            return onFocus(event);
          }
        },
        [onFocus]
      );
      const onBlurCallback = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
          setFocused(false);
          setLength(event?.target?.value?.length || 0);
          if (onBlur) {
            return onBlur(event);
          }
        },
        [onBlur]
      );
      const onChangeCallback = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
          setLength(event?.target?.value?.length || 0);
          if (onChange) {
            return onChange(event);
          }
        },
        [onChange]
      );

      // *** CUSTOM FORM HELPER TEXT IMPLEMENTATION

      const FormHelperTextProps = useMemo(() => {
        return {
          component: FormHelperText,
          length,
          minLength,
          maxLength,
          showCurrentLength,
          showMaxLength,
          ...slotProps?.formHelperText,
        };
      }, [length, minLength, maxLength, showCurrentLength, showMaxLength, slotProps?.formHelperText]);

      // *** FOCUS, SHRINK & ICON HANDLING

      const shrink = useMemo(() => !!(focused || length), [focused, length]);

      const InputLabelProps = useMemo(() => {
        return symbol
          ? {
              shrink,
              style: { marginLeft: shrink ? startAdornmentWidth : 0 },
              ...(deprecatedInputLabelProps as unknown as Record<string, unknown>),
              ...slotProps?.inputLabel,
            }
          : {
              shrink,
              style: { marginLeft: 0 },
              ...(deprecatedInputLabelProps as unknown as Record<string, unknown>),
              ...slotProps?.inputLabel,
            };
      }, [symbol, shrink, startAdornmentWidth, deprecatedInputLabelProps, slotProps?.inputLabel]);

      useEffect(() => {
        setLength(String(props?.value || props?.defaultValue || '').length);
      }, [props?.value, props?.defaultValue]);

      // *** RENDER

      return (
        <MaterialTextField
          ref={ref}
          variant={variant}
          {...props}
          onFocus={onFocusCallback}
          onBlur={onBlurCallback}
          onChange={onChangeCallback}
          error={error}
          helperText={error || showCurrentLength || showMaxLength ? helperText || ' ' : helperText}
          slotProps={{
            ...slotProps,
            input: InputProps,
            htmlInput: HtmlInputProps,
            formHelperText: FormHelperTextProps,
            inputLabel: InputLabelProps,
          }}
          data-test={props?.name}
        />
      );
    }
  )
);

TextField.displayName = 'TextField';
